import { View } from "react-native";
import { Text } from "react-native-paper";
import { VictoryPie } from "victory-native";
import { useAppTheme } from "../../../useAppTheme";
import { CHART_DIMENSIONS } from "../constants";
import type { CircularProgressListData } from "../types";

type CircularProgressListProps = {
	items: CircularProgressListData;
};

type CircularProgressIndicatorProps = {
	progress: number;
	color: string;
	trackColor?: string;
	size?: number;
};

const deriveTrackColor = (hex: string, opacity = 0.2): string => {
	const h = hex.startsWith("#") ? hex : "#dddddd";
	const r = Number.parseInt(h.slice(1, 3), 16);
	const g = Number.parseInt(h.slice(3, 5), 16);
	const b = Number.parseInt(h.slice(5, 7), 16);
	return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const GAP = 3.5;

const CircularProgressIndicator = ({
	progress,
	color,
	trackColor,
	size = CHART_DIMENSIONS.circularProgressList.ringSize,
}: CircularProgressIndicatorProps) => {
	const strokeWidth = 3;
	const clamped = Math.min(Math.max(progress, 0), 100);
	const track = trackColor ?? deriveTrackColor(color);

	const progressArc = Math.max(clamped - GAP, 0.01);
	const remainingArc = Math.max(100 - clamped - GAP, 0.01);

	return (
		<View style={{ width: size, height: size }}>
			<VictoryPie
				data={[
					{ x: "progress", y: progressArc },
					{ x: "gap1", y: GAP },
					{ x: "remaining", y: remainingArc },
					{ x: "gap2", y: GAP },
				]}
				colorScale={[color, "transparent", track, "transparent"]}
				innerRadius={size / 2 - strokeWidth}
				radius={size / 2}
				cornerRadius={strokeWidth / 2}
				width={size}
				height={size}
				padding={0}
				labels={() => null}
				startAngle={0}
				endAngle={360}
			/>
		</View>
	);
};

export const CircularProgressList = ({ items }: CircularProgressListProps) => {
	const theme = useAppTheme();
	const config = CHART_DIMENSIONS.circularProgressList;

	return (
		<>
			{items.map((item, index) => (
				<View
					key={`${item.label}-${index}`}
					style={{
						flexDirection: "row",
						alignItems: "center",
						gap: 16,
						paddingHorizontal: 16,
						paddingVertical: 8,
						minHeight: config.itemMinHeight,
					}}
				>
					<CircularProgressIndicator
						progress={item.progress}
						color={item.color}
						trackColor={item.trackColor}
					/>
					<Text
						variant="bodyLarge"
						style={{
							flex: 1,
							color: theme.colors.onSurface,
						}}
						numberOfLines={1}
					>
						{item.label}
					</Text>
					<Text
						variant="labelSmall"
						style={{ color: theme.colors.onSurfaceVariant }}
					>
						{item.count}
					</Text>
				</View>
			))}
		</>
	);
};
